import { defineComponent as _defineComponent } from 'vue'
import { createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "chat-message__header" }
const _hoisted_2 = { class: "chat-message__avatar" }
const _hoisted_3 = { class: "chat-message__role" }
const _hoisted_4 = {
  key: 0,
  class: "chat-message__mode chat-message__mode--chat"
}
const _hoisted_5 = {
  key: 1,
  class: "chat-message__mode chat-message__mode--code"
}
const _hoisted_6 = {
  key: 2,
  class: "chat-message__mode chat-message__mode--stopped"
}
const _hoisted_7 = { class: "chat-message__time" }
const _hoisted_8 = ["title"]
const _hoisted_9 = { class: "chat-message__body" }
const _hoisted_10 = {
  key: 0,
  class: "chat-message__context"
}
const _hoisted_11 = {
  key: 1,
  class: "chat-message__images"
}
const _hoisted_12 = ["src", "alt"]
const _hoisted_13 = {
  key: 2,
  class: "chat-message__reasoning"
}
const _hoisted_14 = { class: "reasoning-length" }
const _hoisted_15 = { class: "reasoning-content" }
const _hoisted_16 = ["innerHTML"]
const _hoisted_17 = {
  key: 3,
  class: "chat-message__streaming"
}
const _hoisted_18 = {
  key: 4,
  class: "chat-message__changes"
}
const _hoisted_19 = { class: "change-block__header" }
const _hoisted_20 = { class: "change-block__type" }
const _hoisted_21 = { class: "change-block__lines" }
const _hoisted_22 = {
  key: 0,
  class: "change-block__original"
}
const _hoisted_23 = { class: "change-block__code change-block__code--removed" }
const _hoisted_24 = { class: "change-block__new" }
const _hoisted_25 = { class: "change-block__label" }
const _hoisted_26 = { class: "change-block__code change-block__code--added" }
const _hoisted_27 = {
  key: 0,
  class: "chat-message__status chat-message__status--applied"
}
const _hoisted_28 = {
  key: 1,
  class: "chat-message__status chat-message__status--rejected"
}

import { ref, computed } from 'vue'
import { ChatMessage, AiChange, parseAiResponse, getOriginalContent, buildContextLabel } from '../lib/monaco/ai-sidebar-chat'


export default /*@__PURE__*/_defineComponent({
  __name: 'ChatMessageComponent',
  props: {
    message: {}
  },
  emits: ["apply", "reject"],
  setup(__props: any) {

const props = __props



const reasoningExpanded = ref(false)

const formattedTime = computed((): string => {
  const date = new Date(props.message.timestamp)
  return date.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })
})

const contextLabel = computed((): string => {
  if (props.message.context) {
    return buildContextLabel(props.message.context)
  }
  return ''
})

const formattedFirstResponse = computed((): string => {
  if (props.message.firstResponseMs == null) return ''
  return `${(props.message.firstResponseMs / 1000).toFixed(1)}s`
})

const parsedResponse = computed((): { explanation: string; changes: AiChange[] } => {
  if (props.message.role === 'user') {
    return { explanation: props.message.content, changes: [] }
  }
  return parseAiResponse(props.message.content)
})

/** True when AI response is chat-only (no code changes) */
const isChatMode = computed((): boolean => {
  if (props.message.role !== 'assistant') return false
  return parsedResponse.value.changes.length === 0 && props.message.content.length > 0
})

/** True when AI response contains code changes */
const isCodeMode = computed((): boolean => {
  if (props.message.role !== 'assistant') return false
  return parsedResponse.value.changes.length > 0
})

const displayContent = computed((): string => {
  let text = parsedResponse.value.explanation
  if (!text) return ''

  // Normalize \r\n → \n
  text = text.replace(/\r\n/g, '\n')

  // Simple markdown-like rendering
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\n/g, '<br>')
})

const isHandled = computed((): boolean => {
  return !!(props.message.appliedChanges || props.message.rejectedChanges)
})

const changes = computed((): AiChange[] => {
  if (props.message.role === 'user') return []

  const parsed = parsedResponse.value
  // Attach original content from context if available
  if (props.message.context) {
    return parsed.changes.map(change => ({
      ...change,
      originalContent: getOriginalContent(props.message.context!.body, change)
    }))
  }
  return parsed.changes
})

const toggleImageZoom = (event: MouseEvent) => {
  const img = event.target as HTMLImageElement
  img.classList.toggle('chat-message__image--zoomed')
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(['chat-message', __props.message.role === 'user' ? 'chat-message--user' : 'chat-message--ai'])
  }, [
    _createCommentVNode(" Avatar & Role "),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("span", _hoisted_2, _toDisplayString(__props.message.role === 'user' ? '👤' : '🤖'), 1 /* TEXT */),
      _createElementVNode("span", _hoisted_3, _toDisplayString(__props.message.role === 'user' ? 'You' : 'AI'), 1 /* TEXT */),
      (isChatMode.value && __props.message.role === 'assistant' && !__props.message.streaming)
        ? (_openBlock(), _createElementBlock("span", _hoisted_4, " 💬 Ask "))
        : (isCodeMode.value && __props.message.role === 'assistant' && !__props.message.streaming)
          ? (_openBlock(), _createElementBlock("span", _hoisted_5, " ✏️ Agent "))
          : _createCommentVNode("v-if", true),
      (__props.message.role === 'assistant' && __props.message.stoppedByUser)
        ? (_openBlock(), _createElementBlock("span", _hoisted_6, " ⏹ Stopped "))
        : _createCommentVNode("v-if", true),
      _createElementVNode("span", _hoisted_7, _toDisplayString(formattedTime.value), 1 /* TEXT */),
      (__props.message.role === 'assistant' && __props.message.firstResponseMs != null)
        ? (_openBlock(), _createElementBlock("span", {
            key: 3,
            class: _normalizeClass(["chat-message__first-response", { 'chat-message__first-response--live': __props.message.streaming }]),
            title: 'Time to first response: ' + formattedFirstResponse.value
          }, " ⏱ " + _toDisplayString(formattedFirstResponse.value), 11 /* TEXT, CLASS, PROPS */, _hoisted_8))
        : _createCommentVNode("v-if", true)
    ]),
    _createCommentVNode(" Message Content "),
    _createElementVNode("div", _hoisted_9, [
      _createCommentVNode(" Context label for user messages "),
      (__props.message.role === 'user' && contextLabel.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_10, _toDisplayString(contextLabel.value), 1 /* TEXT */))
        : _createCommentVNode("v-if", true),
      _createCommentVNode(" Image attachments "),
      (__props.message.images && __props.message.images.length > 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.message.images, (img, idx) => {
              return (_openBlock(), _createElementBlock("img", {
                key: idx,
                src: img.previewDataUrl || img.url,
                alt: img.filename,
                class: "chat-message__image",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (toggleImageZoom($event)))
              }, null, 8 /* PROPS */, _hoisted_12))
            }), 128 /* KEYED_FRAGMENT */))
          ]))
        : _createCommentVNode("v-if", true),
      _createCommentVNode(" Reasoning/Thinking content (collapsible) "),
      (__props.message.reasoningContent)
        ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
            _createElementVNode("button", {
              class: "reasoning-toggle",
              onClick: _cache[1] || (_cache[1] = ($event: any) => (reasoningExpanded.value = !reasoningExpanded.value))
            }, [
              _createElementVNode("span", null, _toDisplayString(reasoningExpanded.value ? '▼' : '▶'), 1 /* TEXT */),
              _cache[2] || (_cache[2] = _createElementVNode("span", { class: "reasoning-icon" }, "🧠", -1 /* CACHED */)),
              _cache[3] || (_cache[3] = _createElementVNode("span", null, "Thinking", -1 /* CACHED */)),
              _createElementVNode("span", _hoisted_14, "(" + _toDisplayString(__props.message.reasoningContent.length) + " chars)", 1 /* TEXT */)
            ]),
            _withDirectives(_createElementVNode("div", _hoisted_15, [
              _createElementVNode("pre", null, _toDisplayString(__props.message.reasoningContent), 1 /* TEXT */)
            ], 512 /* NEED_PATCH */), [
              [_vShow, reasoningExpanded.value]
            ])
          ]))
        : _createCommentVNode("v-if", true),
      _createCommentVNode(" Text content (explanation) "),
      _createElementVNode("div", {
        class: "chat-message__text",
        innerHTML: displayContent.value
      }, null, 8 /* PROPS */, _hoisted_16),
      _createCommentVNode(" Loading indicator "),
      (__props.message.streaming)
        ? (_openBlock(), _createElementBlock("div", _hoisted_17, [...(_cache[4] || (_cache[4] = [
            _createElementVNode("span", { class: "streaming-dot" }, null, -1 /* CACHED */),
            _createElementVNode("span", { class: "streaming-dot" }, null, -1 /* CACHED */),
            _createElementVNode("span", { class: "streaming-dot" }, null, -1 /* CACHED */)
          ]))]))
        : _createCommentVNode("v-if", true),
      _createCommentVNode(" Changes diff view (only in Code mode) "),
      (changes.value.length > 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(changes.value, (change, index) => {
              return (_openBlock(), _createElementBlock("div", {
                key: index,
                class: _normalizeClass(['change-block', { 'change-block--dimmed': isHandled.value }])
              }, [
                _createElementVNode("div", _hoisted_19, [
                  _createElementVNode("span", _hoisted_20, _toDisplayString(change.type === 'replace' ? '📝 Replace' : '➕ Insert'), 1 /* TEXT */),
                  _createElementVNode("span", _hoisted_21, _toDisplayString(change.type === 'replace'
                ? `Lines ${change.startLine}-${change.endLine}`
                : `At line ${change.startLine}`), 1 /* TEXT */)
                ]),
                _createCommentVNode(" Original content (for replace) "),
                (change.type === 'replace' && change.originalContent)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
                      _cache[5] || (_cache[5] = _createElementVNode("div", { class: "change-block__label" }, "ORIGINAL:", -1 /* CACHED */)),
                      _createElementVNode("pre", _hoisted_23, _toDisplayString(change.originalContent), 1 /* TEXT */)
                    ]))
                  : _createCommentVNode("v-if", true),
                _createCommentVNode(" New content "),
                _createElementVNode("div", _hoisted_24, [
                  _createElementVNode("div", _hoisted_25, _toDisplayString(change.type === 'replace' ? 'NEW:' : 'INSERT:'), 1 /* TEXT */),
                  _createElementVNode("pre", _hoisted_26, _toDisplayString(change.content), 1 /* TEXT */)
                ])
              ], 2 /* CLASS */))
            }), 128 /* KEYED_FRAGMENT */)),
            _createCommentVNode(" Changes are auto-applied as an inline diff in the editor; no buttons needed "),
            (__props.message.appliedChanges)
              ? (_openBlock(), _createElementBlock("div", _hoisted_27, " ✅ 変更を適用しました "))
              : (__props.message.rejectedChanges)
                ? (_openBlock(), _createElementBlock("div", _hoisted_28, " ❌ 変更を破棄しました "))
                : _createCommentVNode("v-if", true)
          ]))
        : _createCommentVNode("v-if", true)
    ])
  ], 2 /* CLASS */))
}
}

})